/**
 * The MCP servers this system talks to.
 *
 * ONE SHAPE FOR ALL OF THEM. Every server in the estate authenticates the
 * same way — client id and secret exchanged at its own token endpoint for a
 * bearer on the transport — so every one of them is configured with the same
 * four values, stored in the same table, and rendered by the same panel.
 *
 * That is a deliberate correction. The RAG server arrived first and got its
 * own columns (`rag_base_url`, `rag_client_id`, …) and its own hand-written
 * form. Adding the Graph server the same way produced `graph_*` columns and
 * a second hand-written form, and the next two servers would have produced
 * four more of each. Adding a server should be an entry in this list, not a
 * migration and a form.
 *
 * A server may need MORE than the common four — the RAG server has a second
 * credential for its push plane and an environment label — and those stay
 * specific to it. What must not vary is the part that is the same.
 */

export interface McpServerSpec {
  key: string;
  /** What it is called on screen. Not the hostname. */
  label: string;
  /** What it does for this system, in one line. */
  purpose: string;
  /** A realistic address, so the field shows the expected shape. */
  placeholder: string;
  /**
   * What happens when it is not configured.
   *
   * Written per server because the honest answer differs: no knowledgebase
   * means articles cannot publish; no directory means nobody is marked as a
   * VIP. Both are safe outcomes rather than failures, and saying so stops an
   * empty field reading as something broken.
   */
  whenAbsent: string;
}

export const MCP_SERVERS: readonly McpServerSpec[] = [
  {
    key: 'rag',
    label: 'Knowledgebase',
    purpose: 'The RAG service every group publishes articles to and the assistant retrieves from',
    placeholder: 'https://wfa-mcp-rag.esa-qa.sitkatech.com',
    whenAbsent: 'Articles still draft and get reviewed; nothing publishes and no suggestions appear.',
  },
  {
    key: 'graph',
    label: 'Directory',
    purpose: 'Microsoft Graph, for resolving job titles so tickets from senior people can be marked',
    placeholder: 'https://wfa-mcp-graph.esa-qa.sitkatech.com',
    whenAbsent: 'Nobody is marked as a VIP — the right answer to not knowing, not a failure.',
  },
];

export const MCP_SERVER_KEYS = MCP_SERVERS.map((s) => s.key);

export function mcpServerSpec(key: string): McpServerSpec | null {
  return MCP_SERVERS.find((s) => s.key === key) ?? null;
}
